import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { AppShell } from "@/components/AppShell";
import { Plus, MessageSquare, Sparkles, Trash2 } from "lucide-react";
import { listThreads, createThread, deleteThread } from "@/lib/coach-store";
import { useEffect, useState } from "react";

export const Route = createFileRoute("/_authenticated/coach/")({
  head: () => ({ meta: [{ title: "AI Coach — LendyTrack" }] }),
  component: CoachIndex,
});

function CoachIndex() {
  const navigate = useNavigate();
  const [threads, setThreads] = useState(() => (typeof window !== "undefined" ? listThreads() : []));

  useEffect(() => {
    if (threads.length === 0) {
      const t = createThread();
      setThreads(listThreads());
      navigate({ to: "/coach/$threadId", params: { threadId: t.id } });
    }
  }, []);

  const startNew = () => {
    const t = createThread();
    navigate({ to: "/coach/$threadId", params: { threadId: t.id } });
  };

  return (
    <AppShell>
      <div className="mb-6 flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-accent">AI Financial Coach</div>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">Your conversations</h1>
        </div>
        <button onClick={startNew} className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90">
          <Plus className="h-4 w-4" /> New chat
        </button>
      </div>

      {threads.length === 0 ? (
        <div className="rounded-2xl border border-border bg-card p-10 text-center shadow-soft">
          <Sparkles className="mx-auto h-8 w-8 text-accent" />
          <h2 className="mt-3 font-display text-xl font-semibold">Start your first chat</h2>
          <p className="mt-1 text-sm text-muted-foreground">Ask about APR, fees, debt strategies, or lender risks.</p>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {threads.map((t) => (
            <div key={t.id} className="group relative rounded-xl border border-border bg-card p-5 shadow-soft hover:shadow-elevated">
              <Link to="/coach/$threadId" params={{ threadId: t.id }} className="block">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <MessageSquare className="h-3 w-3" />
                  <span>{new Date(t.updatedAt).toLocaleDateString()}</span>
                </div>
                <div className="mt-2 font-display text-base font-semibold line-clamp-2">{t.title}</div>
                <div className="mt-1 line-clamp-2 text-xs text-muted-foreground">
                  {t.messages[t.messages.length - 1]?.content ?? "No messages yet"}
                </div>
              </Link>
              <button
                onClick={() => { deleteThread(t.id); setThreads(listThreads()); }}
                className="absolute right-3 top-3 hidden rounded-md p-1.5 text-muted-foreground hover:bg-danger/10 hover:text-danger group-hover:block"
                aria-label="Delete chat"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </AppShell>
  );
}
